import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AgentCard from './AgentCard';
import { useTranslation } from '../../utils/translations';
import { useAuth } from '../../context/AuthContext'; // Updated to use useTeams
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../lib/supabase';
import { fetchActiveAgents } from '../../utils/agentUtils';

const { FiMessageSquare } = FiIcons;

const AgentsList = ({ onAgentSelect, setActiveTab }) => {
  const { user, teams, teamsLoading } = useAuth();
  const t = useTranslation(user?.language);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTeam, setSearchTeam] = useState(''); // New state for team filter

  // Fetch agents whenever active team or search team changes
  useEffect(() => {
    const loadAgents = async () => {
      if (!user) return;
      setLoading(true);
      
      try {
        // Fetch active agents based on team and admin status
        const activeAgents = await fetchActiveAgents(
          searchTeam ? [searchTeam] : teams.map(team => team.id), // Use searchTeam if set
        );
        
        setAgents(activeAgents);
      } catch (error) {
        console.error('Error loading agents:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadAgents();
    
    // Set up real-time subscription to agent changes
    const channel = supabase
      .channel('public:ai_agents_a1b2c3d4e5')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'ai_agents_a1b2c3d4e5' },
        () => {
          console.log('Agents table changed, refreshing agents list');
          loadAgents();
        }
      )
      .subscribe();
      
    return () => {
      supabase.removeChannel(channel);
    };
  }, [searchTeam, user]); // Added searchTeam to dependency array

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-12xl mx-auto">
        {/* Team Selection Dropdown */}
        {!teamsLoading && teams.length > 0 && (
          <div className="mb-6">
            <select
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-hidden focus:ring-2 focus:ring-blue-500"
              onChange={(e) => setSearchTeam(e.target.value)}
              value={searchTeam}
            >
              <option value="">{t('agents.allTeams', 'All Teams')}</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>{team.name}</option>
              ))}
            </select>
          </div>
        )}

        {loading || teamsLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : agents.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-12 text-center border border-gray-200 dark:border-gray-700">
            <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiMessageSquare} className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t('agents.noAgentsAvailable', 'No AI agents available')}
            </h3>
            <p className="text-gray-500 dark:text-gray-400">
              {t('agents.noAgentsMessage', 'No AI agents are available for this team. Contact your administrator to get access.')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {agents.map((agent, index) => (
              <motion.div
                key={agent.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <AgentCard agent={agent} onClick={onAgentSelect} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentsList;